import React, { useState, useEffect, useContext, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Numeral from "../../../components/numeral/Numeral";
import { HttpRequest } from "../../../services/httpRequest";
import { validateSessionAndJwtToken } from "../../../utils/validateTokenSession";
import { CreditUserContext } from "../../../context/CreditUser/CreditUserContext";
import { returnToSignin } from "../../../utils/returnToSign";
import { DashboardMyCredit } from "../../../components/backgrounds/DashboardMyCredit";
import { useCredit } from "../../../context/creditContext";
import { IndexedDBService } from "../../../services/indexedDbService";
import { SkeletonPayloginPage } from "./components/skeleton_paylogin_page";
import { ErrorAlert } from "../../../components/alerts/errorAlert";
import { checkCreditPayable } from "./helpers/payment-gate";
import { useMobileDetection } from "../../../hooks/use-mobile-detection";
import { resolverDeudaHoy } from "../../../utils/deuda.utils";

const indexedDbService = new IndexedDBService();

export function NewPayAbonoPage() {
  const { updateDeudaHoyBackend, updateCreditUser } =
    useContext(CreditUserContext);

  const { disclosures, creditInfo, due } = useCredit();
  const navigate = useNavigate();

  const [basicInfo, setBasicInfo] = useState({});
  const [, setCreditsInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [deudaAFecha, setDeudaAFecha] = useState(0);

  // 🔹 NUEVOS ESTADOS PARA ABONO
  const MIN_ABONO = 3000;
  const [abono, setAbono] = useState("");
  const [abonoNumber, setAbonoNumber] = useState(0);

  const saldoRestante = deudaAFecha - abonoNumber;
  const isValid = abonoNumber >= MIN_ABONO && abonoNumber <= deudaAFecha;

  // { message, key } — la key fuerza a ErrorAlert a montarse de nuevo si el cliente vuelve a intentarlo.
  const [paymentAlert, setPaymentAlert] = useState(null);
  const { isDesktop } = useMobileDetection();
  const [isCheckingPayable, setIsCheckingPayable] = useState(false);

  // El backend confirma que el crédito puede recibir este pago (un abono nunca es novación) antes de pasar
  // a elegir método. Solo bloquea con un rechazo de negocio; ante un fallo técnico deja seguir, porque el
  // paso final (/medios-de-pago) vuelve a validar en el backend.
  const handleAbonarAhora = async () => {
    if (isCheckingPayable) return;
    setIsCheckingPayable(true);
    setPaymentAlert(null);
    try {
      const payable = await checkCreditPayable();
      if (!payable.ok && payable.blocked) {
        setPaymentAlert({ message: payable.message, key: Date.now() });
        return;
      }
      handleUpdateDataIndexDb({
        amount: String(abonoNumber),
        hasExtend: false,
        name: basicInfo.firstname,
        lastname: basicInfo.firstlastname,
      });
      navigate("/medios-de-pago");
    } finally {
      setIsCheckingPayable(false);
    }
  };

  const loadCreditById = async () => {
    try {
      const data = await HttpRequest.getCreditById(
        localStorage.getItem("credit_id"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  };

  const loadInfoUser = async () => {
    try {
      const data = await HttpRequest.getInfoUser(localStorage.getItem("doc"));
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  };

  const loadInfoQuota = async () => {
    try {
      const data = await HttpRequest.getQuotaStatus(
        localStorage.getItem("doc"),
      );
      return data.message.info;
    } catch (error) {
      console.log(error);
    }
  };

  const configData = useCallback(async (credit, infoCupo) => {
    try {
      const result = await resolverDeudaHoy(credit, infoCupo); // ← await + destructuring correcto

      if (result) {
        setDeudaAFecha(result.deudaHoy);
        updateDeudaHoyBackend(result.infoDeudaBackend);
      }
    } catch (error) {
      console.log(error);
    }
  }, [updateDeudaHoyBackend]);

  const handleUpdateDataIndexDb = async (data) => {
    try {
      let item = await indexedDbService.findItem(
        "id",
        localStorage.getItem("credit_id"),
      );
      if (item) {
        indexedDbService.updateItem(item.id, data);
      } else {
        await indexedDbService.addItem({
          ...data,
          id: localStorage.getItem("credit_id"),
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 🔹 SOLO NUMEROS + FORMATO
  const handleAbonoChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");

    if (!value) {
      setAbono("");
      setAbonoNumber(0);
      return;
    }

    let numericValue = parseInt(value, 10);

    if (numericValue > deudaAFecha) {
      numericValue = deudaAFecha;
    }

    setAbonoNumber(numericValue);
    setAbono(new Intl.NumberFormat("es-CO").format(numericValue));
  };

  useEffect(() => {
    const init = async () => {
      try {
        let isValidToken = await validateSessionAndJwtToken();
        if (!isValidToken) {
          returnToSignin(navigate);
          return;
        }

        const info = await loadInfoUser();
        setBasicInfo(info);

        const credit = await loadCreditById();
        const cupo = await loadInfoQuota();

        setCreditsInfo(credit);
        updateCreditUser(credit);

        await configData(credit, cupo);

        setIsLoading(false);
      } catch (error) {
        console.log(error);
        returnToSignin(navigate);
      }
    };

    init();
  }, [configData, navigate, updateCreditUser]);

  if (isLoading) {
    return <SkeletonPayloginPage title={"Abono parcial"} />;
  }

  return (
    <>
      {paymentAlert && (
        <ErrorAlert
          key={paymentAlert.key}
          isOpen={true}
          message={paymentAlert.message}
          isDesktop={isDesktop}
        />
      )}
      <DashboardMyCredit
        name={basicInfo.firstname}
        balance={deudaAFecha}
        current={"2"}
        disclosures={disclosures}
        title={"Abono parcial"}
        creditInfo={creditInfo}
        due={due}
        justify={"center"}
        flexWrap={"wrap"}
        display={"flex"}
        height={"97%"}
        main={
          <div>
            <div className="flex flex-col h-full w-auto bg-white px-8 py-5 rounded-3xl shadow-lg mt-8 md:w-125">
              <div className="flex items-center justify-between px-1 mb-6">
                <button
                  onClick={() => {
                    navigate("/pagar");
                  }}
                  className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Volver"
                  style={{ cursor: "pointer" }}
                >
                  <ArrowLeft className="size-5 text-gray-600" />
                </button>
              </div>
              <h2
                className="text-2xl text-gray-900"
                style={{
                  fontWeight: "500",
                }}
              >
                Abono parcial
              </h2>

              <p className="text-sm text-[#8b94a3] mt-1">
                Ingresa el monto que quieres abonar a tu crédito.
              </p>

              {/* INPUT */}
              <div className="mt-6">
                <label
                  className="text-xs text-[#8b94a3]"
                  style={{
                    fontWeight: "500",
                  }}
                >
                  MONTO DEL ABONO
                </label>

                <div
                  className="mt-2 flex items-center justify-between border rounded-xl px-4 py-3 bg-gray-50"
                  style={{
                    border: "1px solid #8b94a3",
                  }}
                >
                  <span
                    style={{
                      fontSize: "25px",
                      color: "#8b94a3",
                      fontWeight: "bold",
                    }}
                  >
                    $
                  </span>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={abono}
                    onChange={handleAbonoChange}
                    className="focus:ring-0 focus:outline-hidden w-full bg-transparent outline-hidden text-lg ml-2"
                    style={{
                      border: "transparent",
                      fontSize: "27px",
                      fontWeight: "bold",
                      color: "#7f22fe",
                      textAlign: "center",
                    }}
                    placeholder="3.000"
                  />

                  <span
                    style={{
                      fontSize: "25px",
                      color: "#8b94a3",
                      fontWeight: "bold",
                    }}
                  >
                    COP
                  </span>
                </div>
              </div>

              {/* RESUMEN */}
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span style={{ fontSize: "18px", color: "#8b94a3" }}>
                    Deuda total
                  </span>
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "500",
                      color: "#364153",
                    }}
                  >
                    <Numeral format={"$0,0"} value={deudaAFecha} />
                  </span>
                </div>

                <div className="flex justify-between">
                  <span
                    style={{
                      fontSize: "22px",
                      fontWeight: "500",
                      color: "#101828",
                    }}
                  >
                    Saldo restante
                  </span>

                  <span
                    style={{
                      fontSize: "23px",
                      fontWeight: "600",
                      color: "#7f22fe",
                    }}
                  >
                    {saldoRestante > 0 ? (
                      <Numeral
                        format={"$0,0"}
                        value={saldoRestante > 0 ? saldoRestante : 0}
                      />
                    ) : (
                      <span>$0</span>
                    )}
                  </span>
                </div>
              </div>

              {/* BOTON */}
              <button
                disabled={!isValid || isCheckingPayable}
                onClick={handleAbonarAhora}
                className={`mt-6 w-full py-3 rounded-full flex items-center justify-center gap-4 font-semibold`}
                style={{
                  color: "white",
                  backgroundColor: isValid ? "#7f22fe" : "#d2d5d9",
                  cursor: isValid ? "pointer" : "not-allowed",
                  opacity: isCheckingPayable ? 0.7 : 1,
                }}
              >
                <>
                  <span>Abonar ahora</span>{" "}
                  {/* <ChevronRight className={`size-5 text-white`} strokeWidth={2} /> */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </>
              </button>
              <button
                className="w-full py-3 text-sm text-[#8b94a3] hover:text-[#4e5052] transition-colors font-semibold"
                onClick={() => {
                  navigate("/pagar");
                }}
                style={{ cursor: "pointer" }}
              >
                Cancelar
              </button>
            </div>
          </div>
        }
      />
    </>
  );
}
